import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiBriefcase, FiAlertCircle, FiCheck, FiPlus, FiTrash2, FiInfo, FiZap, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import { getAccountOwnerId } from '../utils/ownerEntities';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import BusinessPickerTiles, { CoaSetupEntity, getCoaSetupEntities } from './BusinessPickerTiles';
import {
  getBusinessTypes, getCoaTemplate, createCoaFromTemplate,
  BusiType, TemplateSection,
} from '../coaTemplateApi';
import StagedAccountModal, {
  StagedAccount, StagedAccountSection, StatementCategory, orderWithDepth,
} from './StagedAccountModal';
import './GLChartOfAccountsTab.css';
import { recordTagUse } from '../../../lib/tagStore';

// =============================================
// Setup GL Accounts tab — from the business's type, show the merged starting-COA
// template as an editable/checkable staged list, then create the checked accounts into
// acctg_coa (building a header + child tree). Safe to re-run to add only missing accounts.
// =============================================
interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

// A staged account row + section: shapes shared with the Edit account popup.
type StagedRow = StagedAccount;
type StagedSection = StagedAccountSection;

function resolveAccountOwnerId(users: Record<string, any>[]): number {
  return getAccountOwnerId(users) ?? 0;
}
function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return Number(s.id || s.user_id) || null;
  } catch { return null; }
}

// Which sections (Assets, Liabilities, …) the user keeps folded. A per-browser view
// preference, like the pinned menu: remembered until the user changes it again.
const COLLAPSED_KEY = 'akt_coa_setup_collapsed';
function readCollapsed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]');
    return Array.isArray(v) ? v.map(String) : [];
  } catch { return []; }
}

let rowSeq = 0;
const nextKey = () => `r${rowSeq++}`;

const KIND_BY_SECTION: Record<string, StagedRow['account_kind']> = {
  asset: 'asset', liability: 'liability', equity: 'equity',
  income: 'income', cogs: 'cogs', expense: 'expense',
};

const isBlankRow = (r: StagedRow) => !r.account_name.trim() && !r.suggested_number.trim();

// Every section ends with ONE empty row — "Add another account": clicking it opens the
// account popup, and once it is filled a new empty one follows. An empty row is never
// created (no name) and is not an edit. Its detail type starts as that of the section's
// first account; the popup is where it is chosen.
function withTrailingBlank(sections: StagedSection[]): StagedSection[] {
  return sections.map(s => {
    const last = s.rows[s.rows.length - 1];
    if (last && isBlankRow(last)) return s;
    const sample = s.rows[0];
    return {
      ...s,
      rows: [...s.rows, {
        key: nextKey(), checked: true,
        account_kind: KIND_BY_SECTION[s.key] || 'expense',
        account_name: '', suggested_number: '',
        account_description: '', alias_names: '', tags: '',
        finsta_cate_id: sample ? sample.finsta_cate_id : 14,
        normal_balance: s.normal_balance, exists: false, parent_key: null,
      }],
    };
  });
}

const SetupGlAccountsTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [types, setTypes] = useState<BusiType[]>([]);
  const [coa, setCoa] = useState<Record<string, any>[]>([]);
  const [categories, setCategories] = useState<StatementCategory[]>([]);
  // the account open in the Edit account popup
  const [editing, setEditing] = useState<{ sectionKey: string; rowKey: string } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [sections, setSections] = useState<StagedSection[]>([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed)); } catch { /* preference only */ }
  }, [collapsed]);
  const toggleSection = (sk: string) =>
    setCollapsed(prev => (prev.includes(sk) ? prev.filter(k => k !== sk) : [...prev, sk]));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRows, bizRows, coaRows, cateRows] = await Promise.all([
          loadTable('users').catch(() => getTableData('users')),
          loadTable('user_businesses').catch(() => getTableData('user_businesses')),
          loadTable('acctg_coa').catch(() => getTableData('acctg_coa')),
          loadTable('acctg_finsta_cate').catch(() => getTableData('acctg_finsta_cate')),
        ]);
        const ref = await getBusinessTypes();
        if (cancelled) return;
        setUsers(usersRows); setBusinesses(bizRows); setCoa(coaRows);
        setCategories((cateRows as any[])
          .filter(c => c.is_deleted != 1) // eslint-disable-line eqeqeq
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map(c => ({ cate_id: Number(c.cate_id), header_id: Number(c.header_id), cate_name: String(c.cate_name) })));
        setTypes(ref.types);
        setLoaded(true);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Could not load data.');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accountOwnerId = useMemo(() => resolveAccountOwnerId(users), [users]);

  // People are listed beside businesses: an individual's activity (rental income, a
  // sole proprietorship) gets its own starting accounts for the tax return.
  const bizEntities = useMemo<CoaSetupEntity[]>(
    () => getCoaSetupEntities(viewMode, accountOwnerId, selectedClientId, users, businesses),
    [users, businesses, accountOwnerId, viewMode, selectedClientId]);

  const selectedBiz = useMemo(
    () => bizEntities.find(b => b.key === selectedKey) || null, [bizEntities, selectedKey]);
  // Every type chosen for it (an entity can run several activities), main one first.
  const selectedTypes = useMemo(
    () => (selectedBiz ? selectedBiz.industry_type_ids : [])
      .map(id => types.find(t => t.busi_type_id === id)).filter(Boolean) as BusiType[],
    [selectedBiz, types]);
  const selectedTypeIds = useMemo(() => selectedTypes.map(t => t.busi_type_id), [selectedTypes]);
  const typeIdsKey = selectedTypeIds.join(',');

  useEffect(() => {
    if (bizEntities.length === 1 && selectedKey == null) setSelectedKey(bizEntities[0].key);
  }, [bizEntities, selectedKey]);

  // Existing accounts for the selected owner (for exists-detection / add-missing).
  const existing = useMemo(() => {
    const numbers = new Set<string>();
    const names = new Set<string>();
    if (!selectedBiz || !accountOwnerId) return { numbers, names };
    coa.filter((a: any) =>
      a.is_deleted != 1 && a.account_owner_id == accountOwnerId && // eslint-disable-line eqeqeq
      a.owner_type === selectedBiz.owner_type && a.owner_id == selectedBiz.owner_id) // eslint-disable-line eqeqeq
      .forEach((a: any) => {
        if (a.account_number != null) numbers.add(String(a.account_number));
        if (a.account_name) names.add(String(a.account_name).toLowerCase());
      });
    return { numbers, names };
  }, [coa, selectedBiz, accountOwnerId]);

  // ---- Load the merged template of the selected entity's types ----
  const loadTemplate = useCallback(async (typeIds: number[]) => {
    setTemplateLoading(true);
    setError('');
    try {
      const resp = await getCoaTemplate(typeIds);
      const staged: StagedSection[] = resp.sections.map((s: TemplateSection) => ({
        key: s.key,
        name: s.name,
        normal_balance: s.normal_balance,
        rows: s.accounts.map((a) => {
          const exists = existing.numbers.has(a.suggested_number) || existing.names.has(a.account_name.toLowerCase());
          return {
            key: nextKey(),
            checked: !exists, // pre-check everything not already in the COA
            account_kind: a.account_kind,
            account_name: a.account_name,
            suggested_number: a.suggested_number,
            account_description: a.account_description,
            alias_names: '',
            tags: '',
            finsta_cate_id: a.finsta_cate_id,
            normal_balance: a.normal_balance,
            exists,
            parent_key: null,
          };
        }),
      }));
      setSections(withTrailingBlank(staged));
    } catch (e: any) {
      setError(e?.message || 'Failed to load the starting accounts template.');
      setSections([]);
    } finally {
      setTemplateLoading(false);
    }
  }, [existing]);

  useEffect(() => {
    if (selectedTypeIds.length) loadTemplate(selectedTypeIds);
    else setSections([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeIdsKey]);

  // ---- Staged-list editing ----
  const markDirty = () => setFormDirty(true);
  const updateRow = (sk: string, rk: string, patch: Partial<StagedRow>) => {
    markDirty();
    setSections(prev => withTrailingBlank(prev.map(s => s.key !== sk ? s : {
      ...s, rows: s.rows.map(r => r.key === rk ? { ...r, ...patch } : r),
    })));
  };
  // Removing an account leaves its sub-accounts in the list, directly under the section.
  const deleteRow = (sk: string, rk: string) => {
    markDirty();
    setSections(prev => withTrailingBlank(prev.map(s => s.key !== sk ? s : {
      ...s,
      rows: s.rows.filter(r => r.key !== rk).map(r => (r.parent_key === rk ? { ...r, parent_key: null } : r)),
    })));
  };

  // ---- Edit account popup ----
  const openAccount = (sk: string, rk: string) => setEditing({ sectionKey: sk, rowKey: rk });
  // "Add account" opens the popup on the section's empty row.
  const addAccount = (sk: string) => {
    setCollapsed(prev => prev.filter(k => k !== sk));
    const sec = sections.find(x => x.key === sk);
    const blank = sec ? sec.rows[sec.rows.length - 1] : null;
    if (blank && isBlankRow(blank)) openAccount(sk, blank.key);
  };
  const editingSection = editing ? sections.find(x => x.key === editing.sectionKey) : undefined;
  const editingRow = editing && editingSection ? editingSection.rows.find(r => r.key === editing.rowKey) : undefined;

  // Every named account in the order shown: what the popup's ‹ › arrows walk through.
  const flatAccounts = useMemo(
    () => sections.flatMap(s => orderWithDepth(s.rows).filter(x => !isBlankRow(x.row)).map(x => ({ sectionKey: s.key, rowKey: x.row.key }))),
    [sections]);
  const editingIndex = editing ? flatAccounts.findIndex(x => x.rowKey === editing.rowKey) : -1;

  const saveAccount = (next: StagedRow, targetKey: string, step?: -1 | 1) => {
    if (!editing) return;
    const fromKey = editing.sectionKey;
    // ‹ › : keep the changes and open the neighbour
    const neighbour = step && editingIndex >= 0 ? flatAccounts[editingIndex + step] : undefined;
    markDirty();
    setSections(prev => withTrailingBlank(prev.map(s => {
      if (fromKey === targetKey) {
        return s.key !== targetKey ? s : { ...s, rows: s.rows.map(r => (r.key === next.key ? next : r)) };
      }
      // moved to another account type: out of the old section (its sub-accounts stay
      // there, directly under the section) and in before the new section's empty row
      if (s.key === fromKey) {
        return { ...s, rows: s.rows.filter(r => r.key !== next.key).map(r => (r.parent_key === next.key ? { ...r, parent_key: null } : r)) };
      }
      if (s.key === targetKey) {
        const rows = s.rows.slice();
        const at = rows.length && isBlankRow(rows[rows.length - 1]) ? rows.length - 1 : rows.length;
        rows.splice(at, 0, next);
        return { ...s, rows };
      }
      return s;
    })));
    setEditing(neighbour || null);
  };

  const checkedCount = useMemo(
    () => sections.reduce((n, s) => n + s.rows.filter(r => r.checked && r.account_name.trim()).length, 0),
    [sections]);
  const allCollapsed = sections.length > 0 && sections.every(x => collapsed.includes(x.key));
  const anyExisting = useMemo(() => existing.numbers.size > 0 || existing.names.size > 0, [existing]);

  // ---- Create ----
  const handleCreate = useCallback(async (): Promise<boolean> => {
    if (!selectedBiz || selectedTypeIds.length === 0) return false;
    const accounts = sections.flatMap(s => s.rows
      .filter(r => r.checked && r.account_name.trim())
      .map(r => {
        // a sub-account whose parent is not being created goes directly under the section
        const parent = r.parent_key ? s.rows.find(x => x.key === r.parent_key) : undefined;
        return {
          account_kind: r.account_kind,
          suggested_number: r.suggested_number.trim() || '',
          account_name: r.account_name.trim(),
          account_description: r.account_description,
          alias_names: r.alias_names,
          tags: r.tags,
          finsta_cate_id: r.finsta_cate_id,
          ref: r.key,
          parent_ref: parent && parent.checked && parent.account_name.trim() ? parent.key : null,
        };
      }));
    if (accounts.length === 0) { setError('Check at least one account to create.'); return false; }

    setCreating(true); setError('');
    try {
      const res = await createCoaFromTemplate({
        owner_type: selectedBiz.owner_type,
        owner_id: selectedBiz.owner_id,
        busi_type_id: selectedTypeIds[0],
        accounts,
      });
      setFormDirty(false);
      // the created accounts' tags join / rank up in the account's tag dictionary
      recordTagUse(accounts.map(a => a.tags).filter(Boolean).join(', '));
      setResultMsg(`Created ${res.accounts_created} account${res.accounts_created === 1 ? '' : 's'}` +
        (res.headers_created ? ` under ${res.headers_created} new section header${res.headers_created === 1 ? '' : 's'}` : '') +
        (res.skipped ? ` · skipped ${res.skipped} already-present` : '') + '.');
      setTimeout(() => setResultMsg(''), 5000);

      // Refresh the owner's COA so exists-detection updates + re-run adds only missing.
      const coaRows = await loadTable('acctg_coa').catch(() => getTableData('acctg_coa'));
      setCoa(coaRows);
      await loadTemplate(selectedTypeIds);

      const uid = currentUserId();
      if (uid && uid !== accountOwnerId && accountOwnerId) {
        const actor = users.find((u: any) => Number(u.user_id) === uid);
        const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : 'A user';
        addNotification({
          account_owner_id: accountOwnerId, type: 'coa_from_template',
          title: 'Starting accounts created',
          message: `${actorName} created ${res.accounts_created} starting account(s) for "${selectedBiz.display_name}".`,
          actor_user_id: uid, actor_name: actorName,
        });
      }
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to create accounts.');
      return false;
    } finally {
      setCreating(false);
    }
  }, [selectedBiz, selectedTypeIds, sections, accountOwnerId, users, loadTemplate]);

  useEffect(() => {
    registerSaveHandler(() => handleCreate());
    return () => registerSaveHandler(null);
  }, [handleCreate]);

  // A flag left set when this tab goes away would follow the user to the next screen.
  useEffect(() => () => setFormDirty(false), []);

  // ---- Render ----
  if (!loaded) return <div className="coa-tab"><div className="coa-empty"><p>Loading…</p></div></div>;
  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  return (
    <div className="coa-tab">
      <div className="coa-info-banner">
        <FiZap size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
        Start from a ready-made Chart of Accounts for your business type. Edit names/numbers, uncheck what
        you don't need, then <strong>Create</strong>. You can add more or refine later in <strong>Add New GL Account</strong>.
      </div>

      {error && <div className="coa-field-error" style={{ marginBottom: 12 }}><FiAlertCircle size={14} /> {error}</div>}

      {bizEntities.length === 0 ? (
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiBriefcase size={40} /></div>
          <p>Nobody found. Add a person or a business in <strong>Family &amp; Businesses</strong> first.</p>
        </div>
      ) : (
        <>
          {bizEntities.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <BusinessPickerTiles
                entities={bizEntities}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                typeName={(id) => types.find(t => t.busi_type_id === id)?.type_name}
              />
            </div>
          )}

          {selectedBiz && !selectedBiz.industry_type_id && (
            <div className="coa-empty">
              <div className="coa-empty-icon"><FiInfo size={40} /></div>
              <p>{selectedBiz.is_person
                ? <>Set the type of activity for <strong>{selectedBiz.display_name}</strong> first</>
                : <>Set this business's type first</>} in the <strong>Type of Business</strong> tab to see suggested accounts.</p>
            </div>
          )}

          {selectedBiz && selectedBiz.industry_type_id && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 2px 12px', flexWrap: 'wrap' }}>
                {selectedTypes.map(t => (
                  <span key={t.busi_type_id} className="coa-dropdown-badge">{t.type_name}</span>
                ))}
                {anyExisting && (
                  <span className="coa-field-hint" style={{ color: 'var(--gray-500)' }}>
                    {selectedBiz.is_person ? selectedBiz.display_name : 'This business'} already has some accounts — already-present ones are unchecked.
                  </span>
                )}
              </div>

              {templateLoading ? (
                <div className="coa-empty"><p>Loading suggested accounts…</p></div>
              ) : (
                <>
                  {sections.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 2px 6px' }}>
                      <button type="button" className="coa-section-toggle-all"
                        onClick={() => setCollapsed(allCollapsed ? [] : sections.map(x => x.key))}>
                        {allCollapsed ? 'Expand all' : 'Collapse all'}
                      </button>
                    </div>
                  )}
                  {sections.map(section => {
                    const folded = collapsed.includes(section.key);
                    const named = section.rows.filter(r => r.account_name.trim());
                    const ticked = named.filter(r => r.checked).length;
                    return (
                    <div key={section.key} className="coa-form-card" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: folded ? 0 : 8 }}>
                        <button type="button" className="coa-section-toggle" aria-expanded={!folded}
                          title={folded ? `Show the ${section.name} accounts` : `Hide the ${section.name} accounts`}
                          onClick={() => toggleSection(section.key)}>
                          {folded ? <FiChevronRight size={16} /> : <FiChevronDown size={16} />}
                          <span className="coa-section-toggle-name">{section.name}</span>
                          <span className="coa-field-hint">({section.normal_balance})</span>
                          {folded && <span className="coa-field-hint">· {ticked} of {named.length} selected</span>}
                        </button>
                        <button className="coa-btn-secondary" style={{ fontSize: 13, padding: '4px 10px' }}
                          onClick={() => addAccount(section.key)}>
                          <FiPlus size={13} /> Add account
                        </button>
                      </div>

                      {!folded && orderWithDepth(section.rows).map(({ row, depth }) => {
                        // the empty row at the end: click to add another account
                        if (isBlankRow(row)) {
                          return (
                            <button key={row.key} type="button" className="coa-staged-add"
                              title="Add another account in this category"
                              onClick={() => openAccount(section.key, row.key)}>
                              <FiPlus size={14} /> Add another account…
                            </button>
                          );
                        }
                        const cate = categories.find(c => c.cate_id === Number(row.finsta_cate_id));
                        return (
                          <div key={row.key} className="coa-staged-row" style={{ opacity: row.exists && !row.checked ? 0.6 : 1 }}>
                            <input type="checkbox" checked={row.checked} aria-label={`Create ${row.account_name}`}
                              onChange={e => updateRow(section.key, row.key, { checked: e.target.checked })} />
                            {/* the account itself: one click opens its details */}
                            <button type="button" className="coa-staged-open" title="Edit account"
                              onClick={() => openAccount(section.key, row.key)}>
                              <span className="coa-staged-num">{row.suggested_number}</span>
                              <span className="coa-staged-name" style={{ paddingLeft: depth * 18 }}>
                                {depth > 0 && <span className="coa-staged-sub">↳</span>}
                                {row.account_name}
                                {row.exists && <span className="coa-field-hint"> · already in your Chart of Accounts</span>}
                              </span>
                              {cate && <span className="coa-staged-cate">{cate.cate_name}</span>}
                            </button>
                            <button className="coa-btn-icon danger" title="Remove from list"
                              onClick={() => deleteRow(section.key, row.key)} style={{ width: 28, height: 28 }}>
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    );
                  })}

                  {sections.length > 0 && (
                    <div className="coa-action-bar" style={{ position: 'sticky', bottom: 0 }}>
                      <button className="coa-btn-primary" disabled={checkedCount === 0 || creating}
                        onClick={handleCreate}>
                        <FiCheck size={15} /> {creating ? 'Creating…' :
                          `Create ${checkedCount} account${checkedCount === 1 ? '' : 's'}`}
                      </button>
                      {resultMsg && <span className="coa-saved-msg" style={{ marginLeft: 12 }}>{resultMsg}</span>}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {editing && editingRow && (
        <StagedAccountModal
          key={editingRow.key}
          account={editingRow}
          sectionKey={editing.sectionKey}
          sections={sections}
          categories={categories}
          isNew={isBlankRow(editingRow)}
          position={editingIndex >= 0 ? { index: editingIndex, total: flatAccounts.length } : undefined}
          onSave={saveAccount}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default SetupGlAccountsTab;
